import { memo, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import { CircularLoader } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import useAudienceData from "app/pages/benchmark-tracker/hooks/useAudienceData";
import { useTableHover } from "app/pages/benchmark-tracker/hooks/useTableHover";
import { BenchmarkGranularity } from "app/pages/benchmark-tracker/services/api/constants";
import { selectSelectedBenchmark } from "app/pages/benchmark-tracker/store/selectors/entitySelectors";
import { selectMetricByName } from "app/pages/benchmark-tracker/store/selectors/reportSelectors";
import type { AudienceApiSchema } from "app/pages/benchmark-tracker/types/api.types";

import type { ComponentSchema } from "../../Tab/types";
import { generateColumns } from "../TableView/config";
import TableView from "../TableView/TableView";

import Header from "./Header/Header";
import Legend from "./Legend/Legend";
import SamTamChart from "./SamTamChart/SamTamChart";
import { ChartWrapper, LegendWrapper, LoaderWrapper, TableWrapper, Wrapper } from "./styles";
import { getTableColumns } from "./tableConfig";

const AudienceReach = ({ format, tooltip, title, name, onOpenModal }: ComponentSchema) => {
    const { params } = useCurrentStateAndParams();

    const data = useSelector(selectMetricByName<AudienceApiSchema>(name));

    const selectedBenchmark = useSelector(selectSelectedBenchmark);

    const tableColumns = useMemo(
        () => getTableColumns({ onOtherCompetitorsClick: () => onOpenModal?.(name) }),
        [onOpenModal, name],
    );

    const isLoading = data?.isLoading;

    const widgetData = useAudienceData(data, format, isLoading ?? false);
    const columnsConfig = useMemo(() => generateColumns(tableColumns.names, tableColumns.overwrite), [tableColumns]);
    const { activeRowIndex, onLeave, onRowHover } = useTableHover(widgetData?.tableData);

    const hasBanner = params.granularity === BenchmarkGranularity.WEEKLY;

    return (
        <Wrapper>
            <Header
                title={title}
                value={widgetData?.header.value ?? ""}
                change={widgetData?.header.change ?? ""}
                isIncreasing={widgetData?.header?.isIncreasing ?? false}
                isNotChanged={widgetData?.header?.isNotChanged ?? false}
                tooltip={tooltip ?? ""}
                onOpenModal={() => onOpenModal?.(name)}
                hasBanner={hasBanner}
                isLoading={isLoading ?? false}
            />

            <Flex>
                <ChartWrapper>
                    {isLoading || !widgetData ? (
                        <LoaderWrapper>
                            <CircularLoader isLarge />
                        </LoaderWrapper>
                    ) : (
                        <>
                            <LegendWrapper>
                                <Legend primaryEntityName={selectedBenchmark?.primaryDomains[0] ?? ""} />
                            </LegendWrapper>

                            <SamTamChart data={widgetData?.chartData} />
                        </>
                    )}
                </ChartWrapper>

                <TableWrapper>
                    <TableView
                        tableData={widgetData?.tableData ?? []}
                        columnsConfig={columnsConfig}
                        activeRowIndex={activeRowIndex}
                        onMouseTableLeave={onLeave}
                        onRowHover={onRowHover}
                        isLoading={isLoading ?? false}
                    />
                </TableWrapper>
            </Flex>
        </Wrapper>
    );
};

export default memo(AudienceReach);
